import { alignmentDocumentSchema } from './alignment-document';
import type {
  AlignmentCanonicalPayload,
  CanonicalAlignmentAnswer,
  CanonicalAlignmentCandidateType,
  CanonicalAlignmentCheckpoint,
  CanonicalAlignmentCheckpointRecommendation,
  CanonicalAlignmentEnvelope,
  CanonicalAlignmentVersion,
  CanonicalBreakdownEnvelope,
  CanonicalUnknownObject,
} from './types';
import { parseCanonicalPayloadBase } from './payload-base';

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

function requireRecord(value: unknown, path: string): Record<string, unknown> {
  if (!isRecord(value)) throw new Error(`AI Session ${path} 必须是对象`);
  return value;
}

function requireString(value: unknown, path: string): string {
  if (typeof value !== 'string') throw new Error(`AI Session ${path} 必须是字符串`);
  return value;
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function optionalNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function requireArray(value: unknown, path: string): readonly unknown[] {
  if (!Array.isArray(value)) throw new Error(`AI Session ${path} 必须是数组`);
  return value;
}

function stringList(value: unknown, path: string): readonly string[] {
  const list = requireArray(value, path);
  if (list.some((item) => typeof item !== 'string')) {
    throw new Error(`AI Session ${path} 必须是字符串数组`);
  }
  return list as readonly string[];
}

function parseCandidateTypes(
  value: unknown,
  path: string
): readonly CanonicalAlignmentCandidateType[] {
  const list = requireArray(value, path);
  return list.map((item, index) => {
    const itemPath = `${path}[${index}]`;
    if (typeof item === 'string') {
      const type = item.trim();
      if (!type) throw new Error(`AI Session ${itemPath} 必须是非空字符串`);
      return { type };
    }
    const record = requireRecord(item, itemPath);
    const type = optionalString(record.type);
    if (!type) throw new Error(`AI Session ${itemPath}.type 必须是非空字符串`);
    const label = optionalString(record.label);
    const score = optionalNumber(record.score);
    return {
      type,
      ...(label ? { label } : {}),
      ...(score !== undefined ? { score } : {}),
    };
  });
}

function extensionsOf(
  record: Record<string, unknown>,
  declaredKeys: readonly string[]
): CanonicalUnknownObject | undefined {
  const declared = new Set(declaredKeys);
  const entries = Object.entries(record).filter(([key]) => !declared.has(key));
  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

const ANSWER_KEYS = ['questionId', 'answerType', 'selectedOption', 'content', 'title'] as const;

function parseAnswer(value: unknown, path: string): CanonicalAlignmentAnswer {
  const record = requireRecord(value, path);
  const extensions = extensionsOf(record, ANSWER_KEYS);
  return {
    questionId: requireString(record.questionId, `${path}.questionId`),
    answerType: requireString(record.answerType, `${path}.answerType`),
    ...(optionalString(record.selectedOption)
      ? { selectedOption: optionalString(record.selectedOption) }
      : {}),
    ...(typeof record.content === 'string' ? { content: record.content } : {}),
    ...(optionalString(record.title) ? { title: optionalString(record.title) } : {}),
    ...(extensions ? { extensions } : {}),
  };
}

const VERSION_KEYS = [
  'accepted',
  'savedAt',
  'artifactId',
  'artifactVersion',
  'sessionVersion',
  'editChange',
] as const;

function parseVersion(value: unknown, path: string): CanonicalAlignmentVersion {
  const record = requireRecord(value, path);
  const extensions = extensionsOf(record, VERSION_KEYS);
  return {
    ...(typeof record.accepted === 'boolean' ? { accepted: record.accepted } : {}),
    ...(optionalString(record.savedAt) ? { savedAt: optionalString(record.savedAt) } : {}),
    ...(optionalString(record.artifactId) ? { artifactId: optionalString(record.artifactId) } : {}),
    ...(optionalNumber(record.artifactVersion) !== undefined
      ? { artifactVersion: optionalNumber(record.artifactVersion) }
      : {}),
    ...(optionalNumber(record.sessionVersion) !== undefined
      ? { sessionVersion: optionalNumber(record.sessionVersion) }
      : {}),
    // ADR-client-001 W1：editChange 为声明字段，原样透传（结构解析归消费者 parseAlignmentEditChange）
    ...(isRecord(record.editChange) ? { editChange: record.editChange } : {}),
    ...(extensions ? { extensions } : {}),
  };
}

function parseAlignment(value: unknown): CanonicalAlignmentEnvelope {
  const record = requireRecord(value, 'alignment');
  if (record.skillSource !== 'system' && record.skillSource !== 'custom') {
    throw new Error('AI Session alignment.skillSource 必须是 system 或 custom');
  }
  return {
    answers: requireArray(record.answers, 'alignment.answers').map((answer, index) =>
      parseAnswer(answer, `alignment.answers[${index}]`)
    ),
    alignmentPackage: requireRecord(record.alignmentPackage, 'alignment.alignmentPackage'),
    ...(optionalString(record.alignmentPackageGeneratedAt)
      ? { alignmentPackageGeneratedAt: optionalString(record.alignmentPackageGeneratedAt) }
      : {}),
    ...(optionalString(record.alignmentPackageError)
      ? { alignmentPackageError: optionalString(record.alignmentPackageError) }
      : {}),
    ...(optionalString(record.alignmentPackageFailedAt)
      ? { alignmentPackageFailedAt: optionalString(record.alignmentPackageFailedAt) }
      : {}),
    ...(record.document ? { document: alignmentDocumentSchema.parse(record.document) } : {}),
    compiledSuggestions: requireString(record.compiledSuggestions, 'alignment.compiledSuggestions'),
    ...(isRecord(record.lastAgentProgress) ? { lastAgentProgress: record.lastAgentProgress } : {}),
    versions: requireArray(record.versions, 'alignment.versions').map((version, index) =>
      parseVersion(version, `alignment.versions[${index}]`)
    ),
    skillSnapshot: requireRecord(record.skillSnapshot, 'alignment.skillSnapshot'),
    skillSource: record.skillSource,
    ...(optionalString(record.skillSelectionSource)
      ? { skillSelectionSource: optionalString(record.skillSelectionSource) }
      : {}),
    referencedSessionIds: stringList(record.referencedSessionIds, 'alignment.referencedSessionIds'),
  };
}

function parseBreakdown(value: unknown): CanonicalBreakdownEnvelope {
  const record = requireRecord(value, 'breakdown');
  if (typeof record.confirmed !== 'boolean') {
    throw new Error('AI Session breakdown.confirmed 必须是布尔值');
  }
  if (typeof record.workflowArtifactDirty !== 'boolean') {
    throw new Error('AI Session breakdown.workflowArtifactDirty 必须是布尔值');
  }
  return {
    breakdownStrategy: requireRecord(record.breakdownStrategy, 'breakdown.breakdownStrategy'),
    confirmed: record.confirmed,
    ...(optionalString(record.submittedAt)
      ? { submittedAt: optionalString(record.submittedAt) }
      : {}),
    workflowArtifactDirty: record.workflowArtifactDirty,
    ...(optionalNumber(record.initialBreakdownArtifactVersion) !== undefined
      ? { initialBreakdownArtifactVersion: optionalNumber(record.initialBreakdownArtifactVersion) }
      : {}),
    readingStructured: requireRecord(record.readingStructured, 'breakdown.readingStructured'),
  };
}

const CHECKPOINT_KEYS = [
  'type',
  'runKind',
  'stage',
  'status',
  'terminalReason',
  'waitingAction',
  'suspendReason',
  'recommendation',
  'agentProgress',
  'lastAgentProgress',
] as const;

const CHECKPOINT_STRING_KEYS = CHECKPOINT_KEYS.slice(0, 7);
const CHECKPOINT_RECOMMENDATION_KEYS = [
  'detectedScriptType',
  'detectedScriptTypeLabel',
  'typeConfidence',
  'candidateTypes',
  'evidence',
  'currentSkillLabel',
  'recommendedSkill',
] as const;

function parseCheckpointRecommendation(value: unknown): CanonicalAlignmentCheckpointRecommendation {
  const record = requireRecord(value, 'alignmentCheckpoint.recommendation');
  const extensions = extensionsOf(record, CHECKPOINT_RECOMMENDATION_KEYS);
  return {
    ...(optionalString(record.detectedScriptType)
      ? { detectedScriptType: optionalString(record.detectedScriptType) }
      : {}),
    ...(optionalString(record.detectedScriptTypeLabel)
      ? { detectedScriptTypeLabel: optionalString(record.detectedScriptTypeLabel) }
      : {}),
    ...(optionalNumber(record.typeConfidence) !== undefined
      ? { typeConfidence: optionalNumber(record.typeConfidence) }
      : {}),
    candidateTypes:
      record.candidateTypes === undefined
        ? []
        : parseCandidateTypes(
            record.candidateTypes,
            'alignmentCheckpoint.recommendation.candidateTypes'
          ),
    evidence:
      record.evidence === undefined
        ? []
        : stringList(record.evidence, 'alignmentCheckpoint.recommendation.evidence'),
    ...(optionalString(record.currentSkillLabel)
      ? { currentSkillLabel: optionalString(record.currentSkillLabel) }
      : {}),
    ...(isRecord(record.recommendedSkill) ? { recommendedSkill: record.recommendedSkill } : {}),
    ...(extensions ? { extensions } : {}),
  };
}

function parseCheckpoint(value: unknown): CanonicalAlignmentCheckpoint {
  const record = requireRecord(value, 'alignmentCheckpoint');
  const strings = Object.fromEntries(
    CHECKPOINT_STRING_KEYS.flatMap((key) => {
      const value = optionalString(record[key]);
      return value === undefined ? [] : [[key, value]];
    })
  );
  const extensions = extensionsOf(record, CHECKPOINT_KEYS);
  return {
    ...strings,
    ...(record.recommendation !== undefined
      ? { recommendation: parseCheckpointRecommendation(record.recommendation) }
      : {}),
    ...(isRecord(record.agentProgress) ? { agentProgress: record.agentProgress } : {}),
    ...(isRecord(record.lastAgentProgress) ? { lastAgentProgress: record.lastAgentProgress } : {}),
    ...(extensions ? { extensions } : {}),
  };
}

export function parseAlignmentCanonicalPayload(
  payload: Record<string, unknown>
): AlignmentCanonicalPayload {
  return {
    ...parseCanonicalPayloadBase(payload),
    ...(typeof payload.payloadVersion === 'number'
      ? { payloadVersion: payload.payloadVersion }
      : {}),
    alignment: parseAlignment(payload.alignment),
    breakdown: parseBreakdown(payload.breakdown),
    ...(payload.alignmentCheckpoint !== undefined
      ? { alignmentCheckpoint: parseCheckpoint(payload.alignmentCheckpoint) }
      : {}),
    ...(isRecord(payload.coordinatorHitl) ? { coordinatorHitl: payload.coordinatorHitl } : {}),
    ...(isRecord(payload.contextPolicy) ? { contextPolicy: payload.contextPolicy } : {}),
  };
}
